/**
 * Identities of a client (Pascal, 29/09/2026): its Business OS name, its
 * HubSpot company and its Qonto clients — the state of each link, the text
 * and chip of the Clients list cells, and « Noms à vérifier ». Kept apart
 * from clientModel (no mission code) so the dashboard « Clients N » reads the
 * same reasons with the same labels without loading the Clients screen.
 */
import type { Tone } from '../../components/c';
import { safeString } from '../../lib/format';
import type { TabEntity } from '../../lib/types';

type ClientRow = TabEntity;

export function clientDisplayName(row: ClientRow): string {
  return safeString(row.name).trim() || 'Client sans nom';
}

export function clientHubspotLinked(row: ClientRow): boolean {
  return safeString(row.hubspot_link_status) === 'confirmed' || Boolean(safeString(row.hubspot_url));
}

function records(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? (value.filter((item) => item && typeof item === 'object') as Array<Record<string, unknown>>) : [];
}

export type HubspotLinkReason =
  | 'linked'
  | 'missing'
  | 'pending'
  | 'field_link_disagree'
  | 'company_not_in_projection'
  | 'portal_unverified';
export type QontoLinkReason = 'linked' | 'missing' | 'several';

const HUBSPOT_REASONS: ReadonlySet<string> = new Set([
  'linked', 'missing', 'pending', 'field_link_disagree', 'company_not_in_projection', 'portal_unverified',
]);
const QONTO_REASONS: ReadonlySet<string> = new Set(['linked', 'missing', 'several']);

/** Why the HubSpot link is (not) usable; older payloads fall back on the status. */
export function clientHubspotReason(row: ClientRow): HubspotLinkReason {
  const raw = safeString(row.hubspot_link_reason);
  if (HUBSPOT_REASONS.has(raw)) return raw as HubspotLinkReason;
  return clientHubspotLinked(row) ? 'linked' : 'missing';
}

/** '' when the payload does not say (never guessed as « non lié »). */
export function clientQontoReason(row: ClientRow): QontoLinkReason | '' {
  const raw = safeString(row.qonto_link_reason);
  return QONTO_REASONS.has(raw) ? (raw as QontoLinkReason) : '';
}

export interface ClientQontoIdentity {
  id: string;
  name: string;
  /** This Qonto client's name is clearly different from the Business OS name. */
  nameDiffers: boolean;
}

export function clientQontoClients(row: ClientRow): ClientQontoIdentity[] {
  const items = records(row.qonto_clients)
    .map((item) => ({ id: safeString(item.id).trim(), name: safeString(item.name).trim(), nameDiffers: item.name_differs === true }))
    .filter((item) => item.id);
  // An older payload only says it for the whole row: one linked client is that one.
  if (items.length === 1 && !items[0].nameDiffers && nameMismatch(row, 'qonto') && items[0].name) {
    items[0].nameDiffers = true;
  }
  return items;
}

export function clientHubspotCompanyName(row: ClientRow): string {
  return safeString(row.hubspot_company_name).trim();
}

export function clientHubspotCompanyDomain(row: ClientRow): string {
  return safeString(row.hubspot_company_domain).trim();
}

/** The linked HubSpot company has no synchronised deal: its name was read live in HubSpot (ADR 0004). */
export function clientHubspotOutsideDeals(row: ClientRow): boolean {
  return row.hubspot_company_outside_deals === true;
}

/** The confirmed link to remove before pointing the client elsewhere ('' when none). */
export function clientHubspotConfirmedCompanyId(row: ClientRow): string {
  return safeString(row.hubspot_confirmed_company_id).trim();
}

function nameMismatch(row: ClientRow, connector: 'hubspot' | 'qonto'): boolean {
  const value = row.name_mismatch;
  return Boolean(value && typeof value === 'object' && (value as Record<string, unknown>)[connector] === true);
}

export function clientHubspotNameDiffers(row: ClientRow): boolean {
  return clientHubspotReason(row) === 'linked' && nameMismatch(row, 'hubspot');
}

export function clientQontoNameDiffers(row: ClientRow): boolean {
  return clientQontoReason(row) !== '' && clientQontoReason(row) !== 'missing' && nameMismatch(row, 'qonto');
}

/** The Fibery field, the link and the HubSpot copy do not tell the same story. */
export function clientHubspotIncoherent(row: ClientRow): boolean {
  const reason = clientHubspotReason(row);
  return reason === 'field_link_disagree' || reason === 'company_not_in_projection';
}

export function clientHubspotUnlinked(row: ClientRow): boolean {
  return clientHubspotReason(row) === 'missing';
}

export function clientQontoUnlinked(row: ClientRow): boolean {
  return clientQontoReason(row) === 'missing';
}

/** « Noms à vérifier »: a linked name clearly different, or an incoherent HubSpot link. */
export function clientNamesToCheck(row: ClientRow): boolean {
  return clientHubspotNameDiffers(row) || clientQontoNameDiffers(row) || clientHubspotIncoherent(row);
}

/** The small chip next to an identity name; `action` names its sibling button ('' = no action). */
export interface IdentityChipState {
  label: string;
  tone: Tone;
  title: string;
  action: string;
}

/**
 * What one identity cell of the list shows: the external name as text (read
 * on touch screens too, never only in a tooltip) and, when something needs a
 * look, a small chip next to it. Both may be present, never neither.
 */
export interface IdentityCellState {
  text: string;
  title: string;
  chip: IdentityChipState | null;
}

/**
 * The HubSpot state that asks for action, with the labels of the Clients
 * list; null when the link is fine or cannot be verified (portal).
 * Shared with the dashboard « Clients N » so both screens tell one story.
 */
export function clientHubspotAttention(row: ClientRow): IdentityChipState | null {
  const name = clientDisplayName(row);
  const company = clientHubspotCompanyName(row);
  switch (clientHubspotReason(row)) {
    case 'portal_unverified':
      return null;
    case 'linked':
      return clientHubspotNameDiffers(row)
        ? { label: 'Nom différent', tone: 'billed', title: `Nom dans HubSpot : ${company}`, action: `Vérifier la société HubSpot de ${name}` }
        : null;
    case 'pending':
      return { label: 'En attente', tone: 'muted', title: 'Liaison en attente de confirmation par Fibery', action: `Voir la liaison HubSpot de ${name}` };
    case 'field_link_disagree':
      return { label: 'Lien incohérent', tone: 'alert', title: 'La fiche Fibery et le lien Business OS ne désignent pas la même société HubSpot', action: `Corriger la société HubSpot de ${name}` };
    case 'company_not_in_projection':
      return { label: 'Lien incohérent', tone: 'alert', title: 'La société HubSpot reliée n’a plus d’affaire synchronisée : son nom ne peut pas être vérifié', action: `Corriger la société HubSpot de ${name}` };
    default:
      return { label: 'Non lié', tone: 'alert', title: 'Relier ce client à sa société HubSpot', action: `Relier ${name} à HubSpot` };
  }
}

export function clientHubspotCell(row: ClientRow): IdentityCellState | null {
  if (clientHubspotReason(row) === 'portal_unverified') return null;
  const company = clientHubspotCompanyName(row);
  const domain = clientHubspotCompanyDomain(row);
  const text = company || domain;
  const title = domain && company ? `${company} · ${domain}` : text;
  const chip = clientHubspotAttention(row);
  if (!chip && !text) {
    return { text: '', title: '', chip: { label: 'Lié', tone: 'paid', title: 'Société HubSpot reliée', action: '' } };
  }
  // « Non lié »: a name from nowhere would be a guess.
  return { text: clientHubspotReason(row) === 'missing' ? '' : text, title, chip };
}

export function clientQontoCell(row: ClientRow): IdentityCellState | null {
  const name = clientDisplayName(row);
  const reason = clientQontoReason(row);
  if (!reason) return null;
  if (reason === 'missing') {
    return { text: '', title: '', chip: { label: 'Non lié', tone: 'alert', title: 'Relier ce client à son client Qonto', action: `Relier ${name} à Qonto` } };
  }
  const linked = clientQontoClients(row);
  // The differing name first: it is the one to read (« Noms à vérifier »).
  const ordered = [...linked.filter((item) => item.nameDiffers), ...linked.filter((item) => !item.nameDiffers)];
  const first = ordered[0];
  const firstName = first?.name || '';
  // An older payload may only say it for the whole row: the count still matches « Noms à vérifier ».
  const differs = linked.some((item) => item.nameDiffers) || clientQontoNameDiffers(row);
  if (reason === 'several' && linked.length > 1) {
    const names = ordered.map((item) => `${item.name || 'nom inconnu'}${item.nameDiffers ? ' (nom différent)' : ''}`).join(' · ');
    return {
      text: firstName || 'Nom pas encore lu',
      title: `Clients Qonto reliés : ${names}`,
      chip: {
        label: `+${(linked.length - 1).toLocaleString('fr-FR')}${differs ? ' · nom différent' : ''}`,
        tone: differs ? 'billed' : 'muted',
        title: `Clients Qonto reliés : ${names}`,
        action: `Vérifier les clients Qonto de ${name}`,
      },
    };
  }
  if (differs) {
    return {
      text: firstName,
      title: firstName,
      chip: { label: 'Nom différent', tone: 'billed', title: `Nom dans Qonto : ${firstName}`, action: `Vérifier le client Qonto de ${name}` },
    };
  }
  if (firstName) return { text: firstName, title: firstName, chip: null };
  return { text: '', title: '', chip: { label: 'Lié', tone: 'paid', title: 'Client Qonto relié (nom pas encore lu dans Qonto)', action: '' } };
}
